import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/types/database.generated";
import { fonteParaLeitura } from "../../commons/somenteLeitura";

/**
 * Cliente do Supabase.
 *
 * `createClient` LANÇA quando a URL não está definida, e este módulo é
 * importado estaticamente por dezenas de telas — então uma variável de ambiente
 * faltando não dá uma tela de erro, dá um app que nunca sai do carregando. Foi
 * o que travou a revisão visual do funil: parecia tela de login, era isto.
 *
 * Esse comportamento é o CERTO em dev e em produção: ambiente sem URL é
 * configuração quebrada e tem que doer. Por isso o fallback abaixo é estreito e
 * vale só no modo local sem login (ver `components/app/ModoLocalSemLogin.tsx`),
 * onde não existe banco nenhum para falar — os dados vêm de um provider falso,
 * e este cliente é construído e nunca usado.
 *
 * O endereço de mentira é `localhost` de propósito: se alguma tela escapar e
 * tentar consultar, a requisição morre na própria máquina em vez de sair para a
 * internet.
 */
const modoLocal = import.meta.env.DEV && import.meta.env.VITE_MODO_LOCAL === "1";

const url = import.meta.env.VITE_SUPABASE_URL;
const chaveAnon = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient<Database>(
  url || (modoLocal ? "http://localhost:1" : url),
  chaveAnon || (modoLocal ? "sem-chave-no-modo-local" : chaveAnon),
);

// Perfis de leitura (migration 184) leem views `leitura_*` no lugar de várias
// tabelas. Trocar aqui, num lugar só, cobre toda tela que consulta direto.
const fromOriginal = supabase.from.bind(supabase);
supabase.from = ((tabela: string) => fromOriginal(fonteParaLeitura(tabela) as any)) as typeof supabase.from;
